import 'match-container';

import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import defaultTranslations from '../../public/i18n/en.json';
import { ColorSchemeContext } from './shared/contexts/color-scheme.context';
import { LanguageService } from './shared/services/language.service';
import { SlideOverService } from './shared/services/slide-over.service';

/**
 * The root component that hosts the PublicCoordinate application.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet, MatSidenavModule],
})
export class AppComponent {
  readonly #slideOverService = inject(SlideOverService);
  readonly #router = inject(Router);
  readonly #document = inject(DOCUMENT);
  readonly #translateService = inject(TranslateService);
  readonly #languageService = inject(LanguageService);
  readonly #overlayContainer = inject(OverlayContainer);
  readonly #colorSchemeContext = inject(ColorSchemeContext);

  protected readonly isSlideOverRoute = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => {
        return !event.urlAfterRedirects.startsWith('/project'); // Adjust this condition based on the slide-over routes
      }),
    ),
  );

  protected readonly slideOverIsOpen = this.#slideOverService.isOpen;
  protected readonly slideOverWidth = this.#slideOverService.slideOverWidth;
  protected readonly slideOverColorScheme = this.#colorSchemeContext.override;

  readonly #body: HTMLElement | null =
    this.#document.getElementById('main-body');

  constructor() {
    this.#translateService.setTranslation('en', defaultTranslations);
    this.#translateService.setFallbackLang('en');

    effect(() => {
      const languageCode = this.#languageService.currentLanguage().code;
      if (this.#translateService.getCurrentLang() !== languageCode) {
        this.#translateService.use(languageCode);
      }
    });

    effect(() => {
      if (!this.slideOverIsOpen()) {
        this.#body?.classList.remove('no-scroll');
      } else {
        this.#body?.classList.add('no-scroll');
      }
    });

    effect(() => {
      const colorScheme = this.slideOverColorScheme();
      const overlayClassList =
        this.#overlayContainer.getContainerElement().classList;
      overlayClassList.toggle(
        'kh-slideover--scheme-light',
        colorScheme === 'light',
      );
      overlayClassList.toggle(
        'kh-slideover--scheme-dark',
        colorScheme === 'dark',
      );
    });
  }

  protected onSlideOverClosed() {
    this.#slideOverService.resetSlideOverState();
  }

  protected forceOpenSlideOver() {
    this.#slideOverService.openSlideOver();
  }

  protected forceCloseSlideOver() {
    this.#slideOverService.closeSlideOver();
  }
}
